import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/theme-toggle';
import { useAuth } from '@/contexts/auth-context';
import { Badge } from '@/components/ui/badge';
import { Menu, Briefcase, Users, Star, FileText, Building, BarChart, Info } from 'lucide-react';
import { Logo } from '@/components/logo';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from '@/lib/utils';

// List of public/marketing routes
const PUBLIC_ROUTES = [
  '/',
  '/about',
  '/about/vision',
  '/about/company', 
  '/about/values',
  '/about/blog',
  '/marketing/professionals',
  '/marketing/employers',
  '/pricing',
  '/skills-verification'
];

// Helper function to check if current path matches a public route
const isPublicRoute = (pathname: string) => {
  return PUBLIC_ROUTES.some(route => {
    // Exact match
    if (route === pathname) return true;
    // Check if current path starts with route (for nested routes)
    if (route !== '/' && pathname.startsWith(route)) return true;
    return false;
  });
};

const ListItem = ({ className, title, children, href, icon: Icon, badge }: any) => {
  return (
    <li>
      <Link
        to={href}
        className={cn(
          "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
          className
        )}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-primary" />}
          <div className="text-sm font-medium leading-none">{title}</div>
          {badge && (
            <Badge variant="secondary" className="ml-2 text-xs">
              {badge}
            </Badge>
          )}
        </div>
        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground mt-1">
          {children}
        </p>
      </Link>
    </li>
  );
};

export default function PublicNav() {
  const { user } = useAuth();
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);

  return (
    <header className="border-b bg-background z-40">
      <div className="container flex items-center justify-between h-16 px-4 md:px-6">
        <Link to="/" className="flex items-center">
          <Logo />
        </Link>

        <div className="flex items-center gap-4">
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList className="flex items-center gap-2">
              <NavigationMenuItem>
                <NavigationMenuTrigger className="h-9">About</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4">
                    <ListItem
                      href="/about/vision"
                      title="Our Vision"
                      icon={Info}
                    >
                      Learn about our mission and what drives us
                    </ListItem>
                    <ListItem
                      href="/about/company"
                      title="Company"
                      icon={Building}
                    >
                      Meet our team and company culture
                    </ListItem>
                    <ListItem
                      href="/about/values"
                      title="Values"
                      icon={Star}
                    >
                      Discover the principles that guide us
                    </ListItem>
                    <ListItem
                      href="/about/blog"
                      title="Blog"
                      icon={FileText}
                      badge="New"
                    >
                      Latest news, insights and updates
                    </ListItem>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="h-9">Professionals</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4">
                    <ListItem
                      href="/marketing/professionals"
                      title="For Professionals"
                      icon={Users}
                    >
                      Build your professional brand and advance your career
                    </ListItem>
                    <ListItem
                      href="/skills-verification"
                      title="Skill Verification"
                      icon={Briefcase}
                    >
                      Get your skills validated by experts
                    </ListItem>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="h-9">Employers</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4">
                    <ListItem
                      href="/marketing/employers"
                      title="Hiring Solutions"
                      icon={Building}
                    >
                      Transform your hiring process with AI-powered matching
                    </ListItem>
                    <ListItem
                      href="/pricing"
                      title="Pricing"
                      icon={Star}
                    >
                      View our pricing plans and features
                    </ListItem>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
          
          <ThemeToggle />
          {user ? (
            <Button asChild>
              <Link to="/dashboard">
                Dashboard
              </Link>
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="ghost" asChild>
                <Link to="/login">Sign In</Link>
              </Button>
              <Button asChild>
                <Link to="/waitlist">Join Waitlist</Link>
              </Button>
            </div>
          )}
          <Button variant="ghost" size="icon" className="md:hidden">
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>
      </div>
    </header>
  );
}